/** @jsx jsx */
import { Grid, Icon, NoResults, Spacer } from "@cd2/components"
import { Fields } from "@cd2/forms"
import { titleize } from "@cd2/tools"
import { css, jsx } from "@emotion/core"
import { useMemo, useRef, useState } from "react"
import { EOSPagination } from "../../Components/EOSPagination"
import { brandColors } from "../../Components/EOSThemeProvider"
import { useGlobalState } from "../../Components/GlobalStateProvider"
import { IndexFilterWrapper } from "../../Components/IndexFiltersWrapper"
import { PageWrapper } from "../../Components/PageWrapper"
import { CustomerSelectList2 } from "../../Components/SelectLists/CustomerSelectList2"
import { UserSelectList } from "../../Components/SelectLists/UserSelectList"
import { IStatus, TableWrapper } from "../../Components/TableWrapper"
import { Toolbar } from "../../Components/Toolbar"
import { ISkeemSort } from "../../Components/_createTable"
import { icons } from "../../constants/icons"
import { useAsyncEffect } from "../../hooks/useAsyncEffect"
import { useSiteAlerts } from "../../hooks/useSiteAlerts"
import { eRoutes } from "../../routes"
import { IRecord } from "../../types/schema"
import { rmasTable } from "./RmasTable"
import { IORBranchIRecord, orderRequestBranchAttributes } from "../Branches/BranchesShowPage"
import { BranchSelect } from "../../Components/SelectLists/BranchSelect"

export enum RmaStatus {
  PENDING = "pending",
  RAISED = "raised",
  AWAITING_GOODS_RECEIPT = "awaiting_goods_receipt",
  AWAITING_TRANSFER = "awaiting_transfer",
  AWAITING_PROCESSING = "awaiting_processing",
  AWAITING_REPAIR = "awaiting_repair",
  ACCEPTED_FOR_CREDIT = "accepted_for_credit",
  REJECTED = "rejected",
  CLOSED = "closed",
}

export enum RmaRequestType {
  CREDIT = "credit",
  REPAIR = "repair",
  NOT_APPLICABLE = "not_applicable",
}

export const rmaStatusMessages: Record<RmaStatus, string> = {
  pending: "Pending: Do not ship until confirmed",
  raised: "Raised but not received",
  awaiting_goods_receipt: "Awaiting goods receipt",
  awaiting_transfer: "Awaiting transfer",
  awaiting_processing: "Awaiting processing",
  accepted_for_credit: "Accepted for credit",
  awaiting_repair: "Awaiting Repair",
  rejected: "Rejected",
  closed: "Closed / Complete",
}
export const rmaStatusSimplified: Record<RmaStatus, string> = {
  pending: "Open",
  raised: "Active",
  awaiting_goods_receipt: "Active",
  awaiting_transfer: "Active",
  awaiting_processing: "Active",
  awaiting_repair: "Active",
  accepted_for_credit: "Complete",
  rejected: "Complete",
  closed: "Complete",
}

export const rmaStatuses = Object.values(RmaStatus).map(status => ({
  label: titleize(status),
  value: status,
  message: rmaStatusMessages[status],
}))

const groupings = [
  { name: "Open", statuses: [RmaStatus.PENDING] },
  {
    name: "Active",
    statuses: [
      RmaStatus.RAISED,
      RmaStatus.AWAITING_GOODS_RECEIPT,
      RmaStatus.AWAITING_TRANSFER,
      RmaStatus.AWAITING_PROCESSING,
      RmaStatus.AWAITING_REPAIR,
    ],
  },
  {
    name: "Complete",
    statuses: [RmaStatus.ACCEPTED_FOR_CREDIT, RmaStatus.REJECTED, RmaStatus.CLOSED],
  },
]
export const rmaStatusesGrouped = groupings.map(status => ({
  label: status.name,
  options: status.statuses.map(status => ({
    label: titleize(status),
    value: status,
    message: rmaStatusMessages[status],
  })),
}))

export enum RmaReturnReason {
  NOT_NEEDED = "not_needed",
  FAULTY = "faulty",
}

export const rmaReturnReasonMessage: Record<RmaReturnReason, string> = {
  not_needed: "Not needed",
  faulty: "Faulty",
}

export const rmaReturnReasons = Object.values(RmaReturnReason).map(status => ({
  label: titleize(status),
  value: status,
  message: rmaReturnReasonMessage[status],
}))
export const rmaRequestTypeLabels: Record<RmaRequestType, string> = {
  [RmaRequestType.CREDIT]: "Credit",
  [RmaRequestType.REPAIR]: "Repair",
  [RmaRequestType.NOT_APPLICABLE]: "N/A",
}

export const rmaRequestTypes = (branch?: boolean) =>
  (Object.values(RmaRequestType) as RmaRequestType[])
    .map(value => ({
      label: rmaRequestTypeLabels[value],
      value,
    }))
    .filter(x => (!branch ? x.value !== RmaRequestType.NOT_APPLICABLE : true))

export function rmaRequestTypeDisplay(requestType: string | undefined | null): string {
  if (!requestType) return ""
  const label = rmaRequestTypeLabels[requestType as RmaRequestType]
  if (label !== undefined) return label
  return titleize(requestType)
}

export const rmaCustomerAttributes = ["name"]
export type IRmaCustomer = IRecord<"customers", "name">
export const rmaSubCustomerAttributes = ["name"]
export type IRmaSubCustomer = IRecord<"sub_customers", "name">
export const rmaContactAttributes = ["name", "email"]
export type IRmaContact = IRecord<"contacts", "name" | "email">
export const rmaBranchAttributes = ["name"]
export type IRmaBranch = IRecord<"branches", "name">

export const rmaOrderItemAttributes = [
  "quantity",
  "stock_code",
  "cost_price_pennies",
  "price_pennies",
  "product",
]
export type IRmaOrderItem = IRecord<
  "order_items",
  "quantity" | "stock_code" | "cost_price_pennies" | "product" | "price_pennies"
>
export const rmaOrderAttributes = [
  "identifier",
  { name: "branch", attributes: ["name"] },
  {
    name: "items",
    attributes: rmaOrderItemAttributes,
  },
]
export type IRmaOrder = IRecord<"orders", "identifier"> & {
  items: IRmaOrderItem[]
  branch: IRecord<"branches", "name">
}
export const rmaAttachmentAttributes = ["fileboyId", "fileName", "created_at", "notes"]
export type IRmaAttachment = IRecord<"files", "fileboyId" | "fileName" | "created_at" | "notes">
export const rmaSupportTicketAttributes = ["identifier"]
export type IRmaSupportTicket = IRecord<"support_tickets", "identifier">
export const rmaAssignedToAttributes = ["name", "email"]
export type IRmaAssignedTo = IRecord<"skeem_users", "name" | "email">
export const rmaProductAttributes = [
  "stock_code",
  "body",
  "product_master",
  "branch",
  { name: "product_group", attributes: ["rma_required_tech_case_number", "rma_return_message"] },
]
export type IRmaProduct = IRecord<
  "products",
  "stock_code" | "body" | "product_master" | "branch"
> & {
  product_group?: IRecord<"product_groups", "rma_required_tech_case_number" | "rma_return_message">
}
export type IRmaItemOrder = IRecord<
  "orders",
  "identifier" | "customer" | "purchase_order_reference"
> & {
  invoice_number?: string
  invoice_date?: string
  delivery?: IRecord<"deliveries", "confirmed_at"> | null
}
export const rmaItemOrderAttributes = [
  "identifier",
  "purchase_order_reference",
  {
    as: "invoice_number",
    value: {
      first: {
        path: [
          {
            name: "invoice_items",
            filter: { matches: { invoice_document_number: { not: "" } } },
          },
          "invoice_document_number",
        ],
      },
    },
  },
  { as: "invoice_date", value: { first: { path: ["invoice_items", "invoice_date"] } } },
  { name: "delivery", attributes: ["confirmed_at"] },
  { name: "customer" },
]

export const rmaItemAttributes = [
  "quantity",
  "serial_number",
  "tech_case_number",
  "item_type",
  "comment",
  "created_at",
  { name: "product", attributes: rmaProductAttributes },
  { name: "order", attributes: rmaItemOrderAttributes },
]
export type IRmaItem = IRecord<
  "rma_items",
  "quantity" | "serial_number" | "tech_case_number" | "item_type" | "comment"
> & {
  order?: IRmaItemOrder
  product?: IRmaProduct
  _delete?: boolean
  created_at?: string
}

export type IRmaRecord = IRecord<
  "rmas",
  | "additionalDetails"
  | "identifier"
  | "orderDate"
  | "returnsNumber"
  | "salesNumber"
  | "supportTicket"
  | "technicalCaseNumber"
  | "created_at"
  | "updated_at"
  | "internalRmaBranch"
  | "closedReason"
  | "statusMessage"
  | "notes"
  | "show_notes_on_confirmation"
  | "order"
> & {
  requestType: RmaRequestType
  returnReason: RmaReturnReason
  status: RmaStatus
  order?: IRmaOrder
  customer?: IRmaCustomer
  sub_customer?: IRmaSubCustomer
  contact?: IRmaContact
  created_by?: IRecord<"skeem_users", "name" | "email">
  items?: IRmaItem[]
  branch?: IRmaBranch
  attachments: IRmaAttachment[]
  assignedTo?: IRmaAssignedTo
  supportTicket?: IRmaSupportTicket
  internalRmaBranch?: IORBranchIRecord
  return_request_order?: IRecord<"orders", "identifier"> & {
    branch: IRecord<"branches", "name">
  }
}
export const rmaRecordAttributes = [
  "creditNumber",
  "identifier",
  "returnReason",
  "rejectReason",
  "additionalDetails",
  "orderDate",
  "returnsNumber",
  "salesNumber",
  "technicalCaseNumber",
  "created_at",
  "updated_at",
  "status",
  "requestType",
  "notes",
  "show_notes_on_confirmation",
  { name: "internalRmaBranch", attributes: orderRequestBranchAttributes },
  "closedReason",
  "statusMessage",
  { name: "items", attributes: rmaItemAttributes, sort: { by: "created_at", direction: "asc" } },
  { name: "attachments", attributes: rmaAttachmentAttributes },
  { name: "assignedTo", attributes: rmaAssignedToAttributes },
  { name: "supportTicket", attributes: rmaSupportTicketAttributes },
  { name: "customer", attributes: rmaCustomerAttributes },
  { name: "sub_customer", attributes: rmaSubCustomerAttributes },
  { name: "contact", attributes: rmaContactAttributes },
  { name: "branch", attributes: rmaBranchAttributes },
  { name: "order", attributes: rmaOrderAttributes },
  { name: "created_by", attributes: ["name", "email"] },
  {
    name: "return_request_order",
    attributes: ["identifier", { name: "branch", attributes: ["name"] }],
  },
]

interface IFilter {
  query: string
  productQuery: string
  sort: ISkeemSort
  pagination: { page: number; perPage: number }
  customerId?: string
  branch?: string
  assignedToId?: string
  status?: RmaStatus
  returnReason?: RmaReturnReason
}
interface IFetchResult {
  records: IRmaRecord[]
  count: number
  rawCount: any
}
const defaultSort: ISkeemSort = { by: "created_at", direction: "desc" }
export function RmasIndexPage() {
  const alerts = useSiteAlerts()
  const [status, setStatus] = useState<IStatus>("loading")
  const [totalCount, setTotalCount] = useState(0)
  const debounceRef = useRef<any>(null)
  const { currentUser } = useGlobalState()
  const skeem = window.skeem

  const [records, setRecords] = useState<IRmaRecord[] | null>(null)

  const [filter, _setFilter] = useState<IFilter>({
    query: "",
    productQuery: "",
    sort: defaultSort,
    pagination: { page: 1, perPage: 25 },
    branch: currentUser.branch?.id ?? undefined,
  })
  function setFilter(partialFilter: Partial<IFilter>) {
    return _setFilter(filter => ({ ...filter, ...partialFilter }))
  }

  const { query, productQuery, pagination } = filter

  const [forkedQuery, setForkedQuery] = useState(query)
  const [forkedProductQuery, setForkedProductQuery] = useState(productQuery)
  function handleSearchProductChange(query: string) {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }
    setForkedProductQuery(query)
    debounceRef.current = setTimeout(() => {
      setFilter({ productQuery: query })
    }, 300)
  }

  const paginationProps = useMemo(
    () => ({
      currentPage: pagination.page,
      gotoPage: (page: number) =>
        setFilter({ pagination: { perPage: filter.pagination.perPage, page } }),
      perPage: pagination.perPage,
      setPerPage: (perPage: number) => setFilter({ pagination: { page: 1, perPage } }),
      maxPages: Math.ceil((records ? totalCount : 0) / pagination.perPage),
    }),
    [pagination.page, pagination.perPage, records ? totalCount : 0],
  )

  const fetchFilter = () => ({
    and: [
      filter.status && { matches: { status: filter.status } },
      filter.returnReason && { matches: { returnReason: filter.returnReason } },
      filter.assignedToId && { matches: { assignedTo: filter.assignedToId } },
      filter.customerId && { matches: { customer: filter.customerId } },
      filter.branch && { matches: { branch: filter.branch } },
      query && {
        or: [
          { matches: { identifier: { like: query } } },
          { like: [{ path: ["order", "identifier"] }, { value: query }] },
          { matches: { salesNumber: { like: query } } },
          { matches: { technicalCaseNumber: { like: query } } },
        ].filter(Boolean),
      },
      productQuery && {
        gte: [
          {
            count: {
              path: [
                "items",
                {
                  name: "product",
                  filter: { matches: { stock_code: { like: productQuery } } },
                },
              ],
            },
          },
          { value: 1 },
        ],
      },
    ].filter(Boolean),
  })

  async function fetchRecords(): Promise<IFetchResult | null> {
    let fetchResult
    try {
      fetchResult = await skeem.fetch2<IFetchResult>({
        obj: {
          records: {
            model: {
              name: "rmas",
              attributes: rmaRecordAttributes,
              paginate: { page: pagination.page, size: pagination.perPage },
              filter: fetchFilter(),
              sort: rmasTable.buildSortOption(filter.sort.by, filter.sort.direction),
            },
          },
          count: {
            count: {
              name: "rmas",
              filter: fetchFilter(),
            },
          },
        },
      })
      // onLoad?.(fetchResult)
      return fetchResult
    } catch (e) {
      console.log("fetching data error", e)
      alerts.genericError("Error fetching data, please try again")
      return null
    }
  }

  useAsyncEffect(
    async ({ isAlive }) => {
      if (status === "done") {
        setStatus("reloading")
      }
      const fetchResult = await fetchRecords()
      if (fetchResult && isAlive()) {
        setRecords(fetchResult.records)
        setTotalCount(fetchResult.count)
      }
      setStatus("done")
    },
    [
      filter.query,
      filter.productQuery,
      JSON.stringify(filter.sort),
      filter.assignedToId,
      filter.customerId,
      filter.returnReason,
      filter.status,
      filter.branch,
      filter.pagination.page,
    ],
  )

  function handleSearchChange(query: string) {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }
    setForkedQuery(query)
    debounceRef.current = setTimeout(() => {
      setFilter({ query })
    }, 300)
  }

  const searchPending = forkedQuery !== query || status === "reloading"
  const tableStatus =
    status === "loading"
      ? "loading"
      : status === "reloading"
      ? "reloading"
      : totalCount === 0 && query !== ""
      ? "noResultsInFilters"
      : totalCount === 0
      ? "noResults"
      : "done"
  return (
    <div>
      <Toolbar items={[Toolbar.createLink({ to: eRoutes["/customer-support/rma/new"] })]} />
      <PageWrapper title="RMA" subtitle="View and manage RMA records." breadcrumbPath={["rma"]}>
        <Grid gap={12}>
          <div
            css={css`
              gap: 8px;
              display: flex;
              flex-wrap: wrap;
              justify-content: space-between;
              align-items: end;
            `}
          >
            <IndexFilterWrapper>
              <Fields.Search
                label="Search"
                value={forkedQuery}
                onChange={handleSearchChange}
                suffix={
                  <Icon icon={searchPending ? icons.spinner : icons.search} spin={searchPending} />
                }
              />
              <Spacer />
              <Fields.Search
                label="Search by Product Code"
                value={forkedProductQuery}
                onChange={handleSearchProductChange}
                suffix={
                  <Icon icon={searchPending ? icons.spinner : icons.search} spin={searchPending} />
                }
              />
              <Spacer />
              <BranchSelect
                label="For Branch"
                onChange={branch => setFilter({ branch: branch?.id })}
                value={filter.branch}
                isClearable
              />
              <Spacer />
              <UserSelectList
                label="Assigned To"
                onChange={id => setFilter({ assignedToId: id })}
                value={filter.assignedToId}
                branchId={filter.branch}
                filter={{ matches: { userAccountType: "branch" } }}
                isClearable
              />
              <Spacer />
              <CustomerSelectList2
                label="Filter by Customer"
                onChange={id => setFilter({ customerId: id })}
                value={filter.customerId}
                allBranches={!filter.branch}
                branchId={filter.branch}
                includeBranchName
                isClearable
              />
              <Spacer />
              <Fields.Select2
                label="Return Reason"
                onChange={status => setFilter({ returnReason: status?.value })}
                value={rmaReturnReasons.find(x => x.value === filter.returnReason)}
                options={rmaReturnReasons}
                isClearable
              />
              <Spacer />
              <Fields.Select2
                label="Status"
                onChange={status => setFilter({ status: status?.value })}
                value={rmaStatuses.find(x => x.value === filter.status)}
                options={rmaStatuses}
                isClearable
              />
              <Spacer />
            </IndexFilterWrapper>
            <EOSPagination {...paginationProps} />
          </div>

          <TableWrapper status={tableStatus} renderNoResults={() => <NoResults />} minWidth={1200}>
            <rmasTable.Table
              css={css`
                .active {
                  background-color: ${brandColors.greenLightBackground};
                }
                .complete {
                  background-color: ${brandColors.blueLightBackground};
                }
                .awaiting_transfer {
                  background-color: ${brandColors.archived};
                }
              `}
              rowClass={(data: IRmaRecord) =>
                data.status === RmaStatus.AWAITING_TRANSFER
                  ? RmaStatus.AWAITING_TRANSFER
                  : rmaStatusSimplified[data.status]?.toLowerCase()
              }
              data={records ?? []}
              sortState={[filter.sort, sort => setFilter({ sort })]}
              columns={rmasTable.allColumns}
            />
          </TableWrapper>
          <EOSPagination {...paginationProps} />
        </Grid>
      </PageWrapper>
    </div>
  )
}
